/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useMemo } from 'react';
import { Box, VStack, Flex } from 'chakra-v3';
import { Text } from '../../components/Text';
import Dropdown from './Dropdown';
import { Carousel } from './Carousel';
import { PromptCard } from './PromptCard';
import benchmarkData from '../../data/benchmarkData.json';

type Domain = 'education' | 'healthcare' | 'finance' | 'legal';

type Language =
  | 'english'
  | 'assamese'
  | 'bengali'
  | 'gujarati'
  | 'hindi'
  | 'kannada'
  | 'malayalam'
  | 'marathi'
  | 'punjabi'
  | 'tamil'
  | 'telugu';

// Language order matching Figma design: English (active), Assamese, Bengali, Gujarati, Hindi, Kannada, Punjabi, Telugu, Malayalam, Tamil, Marathi
const LANGUAGES: Array<{ value: Language; label: string }> = [
  { value: 'english', label: 'English' },
  { value: 'assamese', label: 'Assamese' },
  { value: 'bengali', label: 'Bengali' },
  { value: 'gujarati', label: 'Gujarati' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'kannada', label: 'Kannada' },
  { value: 'punjabi', label: 'Punjabi' },
  { value: 'telugu', label: 'Telugu' },
  { value: 'malayalam', label: 'Malayalam' },
  { value: 'tamil', label: 'Tamil' },
  { value: 'marathi', label: 'Marathi' },
];

interface PromptCard {
  queries: string;
}

// Smaller font size for Tamil text to prevent overflow
const TAMIL_FONT_SIZE = { base: '11px', sm: '12px', md: '13px', lg: '13px' };

const BenchmarkDatasetPreview: React.FC = () => {
  const [selectedDomain, setSelectedDomain] = useState<Domain>('education');
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('english');

  // Filter data based on selected domain and language
  const filteredCards = useMemo<PromptCard[]>(() => {
    const langData = benchmarkData[selectedLanguage as keyof typeof benchmarkData];
    if (!langData) return [];

    const domainData = langData[selectedDomain as keyof typeof langData];
    if (!domainData || !Array.isArray(domainData)) return [];

    return domainData;
  }, [selectedDomain, selectedLanguage]);

  return (
    <Box
      bg="transparent"
      w="100%"
      py={{ base: 8, md: '60px' }}
      px={{ base: 4, md: 6 }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap="44px"
    >
      {/* Title */}
      <Text
        fontFamily="heading"
        fontSize={{ base: '24px', sm: '28px', md: '32px', lg: '40px' }}
        fontWeight="300"
        lineHeight="normal"
        color="white"
        textAlign="center"
        letterSpacing="-1px"
        whiteSpace="pre-wrap"
      >
        Benchmark Dataset Preview
      </Text>

      <VStack gap="60px" alignItems="center" w="100%">
        {/* Domain Selector and Language Grid */}
        <VStack gap="40px" alignItems="center" w="100%">
          {/* Domain Selector */}
          <Dropdown
            options={[
              { id: 'education', label: 'Education', hasCheckbox: true },
              { id: 'healthcare', label: 'Healthcare' },
              { id: 'finance', label: 'Finance' },
              { id: 'legal', label: 'Legal' },
            ]}
            selectedValue={selectedDomain}
            onSelect={(value) => setSelectedDomain(value as Domain)}
          />

          {/* Language Buttons - Responsive wrap for mobile/tablet */}
          <Flex
            gap={{ base: '10px', sm: '12px', md: '14px' }}
            justifyContent="center"
            alignItems="center"
            wrap="wrap"
            w="100%"
            maxW="970px"
          >
            {LANGUAGES.map((lang) => {
              const isSelected = selectedLanguage === lang.value;
              return (
                <Box
                  key={lang.value}
                  as="button"
                  onClick={() => setSelectedLanguage(lang.value)}
                  bg={isSelected ? '#022a35' : 'transparent'}
                  border="0.8px solid"
                  borderColor={isSelected ? 'rgba(10, 49, 60, 0.1)' : 'rgba(255, 255, 255, 0.1)'}
                  borderRadius="4px"
                  h={{ base: '42px', sm: '44px', md: '48px' }}
                  minW={{ base: '46%', sm: '42%', md: '128px' }}
                  px={{ base: 3, md: '8px' }}
                  py={{ base: 2, md: '8px' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: isSelected ? 'rgba(10, 49, 60, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                  }}
                  _focusVisible={{
                    outline: '2px solid',
                    outlineColor: 'rgba(255, 255, 255, 0.5)',
                    outlineOffset: '2px',
                  }}
                >
                  <Text
                    fontFamily="body"
                    fontSize={{ base: '14px', sm: '15px', md: '16px' }}
                    fontWeight="600"
                    lineHeight="1.4"
                    letterSpacing="0.08px"
                    color="white"
                    whiteSpace="nowrap"
                  >
                    {lang.label}
                  </Text>
                </Box>
              );
            })}
          </Flex>
        </VStack>

        {/* Carousel */}
        {filteredCards.length > 0 && (
          <Box
            w="100vw"
            position="relative"
            overflow="hidden"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Carousel
              items={filteredCards}
              visibleCount={{ base: 1.4, md: 2.1, xl: 2.1 }}
              itemWidth={{ md: '559px', xl: '559px' }}
              gap={40}
              renderItem={(card, index) => (
                <Box
                  key={index}
                  w="full"
                  h={{ base: 'auto', md: '387px' }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexShrink={0}
                >
                  <PromptCard
                    queries={card.queries}
                    fontSize={selectedLanguage === 'tamil' ? TAMIL_FONT_SIZE : undefined}
                  />
                </Box>
              )}
            />
          </Box>
        )}

        {/* Empty State */}
        {filteredCards.length === 0 && (
          <Box
            bg="#f9f9f9"
            border="1px solid"
            borderColor="rgba(10, 49, 60, 0.25)"
            borderRadius="4px"
            p={10}
            w="100%"
            maxW="559px"
          >
            <Text
              fontFamily="body"
              fontSize={{ base: '16px', md: '16px', lg: '16px' }}
              fontWeight="normal"
              lineHeight="1.6"
              color="#1a1a1a"
              textAlign="center"
            >
              No data available for the selected domain and language combination.
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default BenchmarkDatasetPreview;
